<template>
  <div class="grid md:grid-cols-5 gap-30">
    <form
      class="grid gap-20 box box-padding bg-white md:col-span-3"
      @submit.prevent="submit"
    >
      <div
        v-if="signinError"
        class="mb-20 bg-red-light text-red p-10 text-base font-normal"
      >
        {{ signinError }}
      </div>

      <FormKitPharmasuisseUser v-model="username" />
      <FormKit
        id="current-password"
        v-model="password"
        :label="$texts('password', 'Passwort')"
        required
        autocomplete="current-password"
        name="current-password"
        type="password"
      />
      <div class="flex gap-10 items-center col-span-full">
        <input
          type="submit"
          class="button is-primary"
          :value="$texts('login', 'Anmelden')"
          :class="{ 'is-loading': isLoading }"
        />
        <NuxtLink :to="passwordResetRoute" class="link">
          {{ $texts('forgotPassword', 'Passwort vergessen') }}
        </NuxtLink>
      </div>
      <div v-if="loginHelpUrl" class="flex gap-10 items-center col-span-full">
        <NuxtLink :to="loginHelpUrl" class="link text-xs flex items-center">
          <SpriteSymbol name="arrow" class="h-10 w-20 mr-10" />
          <span>{{ $texts('loginHelp', 'Hilfestellung zum login') }}</span>
        </NuxtLink>
      </div>
    </form>

    <div
      class="box box-padding md:flex md:flex-col md:col-span-2 md:items-stretch md:flex-1 justify-stretch"
      :class="signupBoxClasses"
    >
      <div class="md:flex md:flex-col flex-1 relative z-20">
        <h2
          class="font-semibold text-2xl mb-10"
          :class="{ 'text-white': textIsWhite }"
        >
          {{ signupBoxTitle }}
        </h2>
        <p v-if="signupBoxText" :class="{ 'text-white': textIsWhite }">
          {{ signupBoxText }}
        </p>
        <div v-else class="content" v-html="createGuestAccountText" />
        <div class="mt-auto">
          <ButtonStyle v-slot="{ classes }" v-bind="buttonProps">
            <NuxtLink :to="signupCtaLink" :class="classes">{{
              signupCtaText
            }}</NuxtLink>
          </ButtonStyle>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useGlobalConfig } from '~/stores/globalConfig'

const route = useRoute()
const { $texts } = useNuxtApp()
const globalConfig = useGlobalConfig()

const props = defineProps<{
  successUrl?: string

  /**
   * The name of the role that should influence the signup CTA.
   */
  targetRole?: string
}>()

const username = ref('')
const password = ref('')
const signinError = ref('')
const providedUser = useQueryString('user')
const isLoading = ref(false)

const buttonProps = computed(() => {
  if (props.targetRole === 'affiliated_pharmacy') {
    return { berry: true, inverted: true }
  } else if (props.targetRole === 'individual_member') {
    return { orange: true, inverted: true }
  }
  return {}
})

const textIsWhite = computed(() => {
  return (
    props.targetRole === 'affiliated_pharmacy' ||
    props.targetRole === 'individual_member'
  )
})

const signupBoxClasses = computed(() => {
  if (props.targetRole === 'affiliated_pharmacy') {
    return 'bg-gradient-to-r relative logo-background from-berry to-pink'
  } else if (props.targetRole === 'individual_member') {
    return 'bg-gradient-to-r relative logo-background from-orange-light to-orange'
  }
  return 'bg-white'
})

const loginHelpUrl = computed(() => globalConfig.linkToLoginHelp || '/')

const signupBoxTitle = computed(() => {
  if (props.targetRole === 'affiliated_pharmacy') {
    return $texts('login.signupTitleForPharmacy', 'Noch kein Konto?')
  }

  return $texts('login.signupTitle', 'Noch kein Konto?')
})

const signupBoxText = computed(() => {
  if (props.targetRole === 'affiliated_pharmacy') {
    return $texts(
      'login.signupTextForPharmacy',
      'Jetzt registrieren und von diversen Vorteilen profitieren.',
    )
  }
})

const createGuestAccountText = computed(() => {
  return globalConfig.createGuestAccountText
})

const signupCtaText = computed(() => {
  if (props.targetRole === 'affiliated_pharmacy') {
    return $texts('signupBoxCtaForPharmacy', 'Jetzt Apotheke anmelden')
  }

  return $texts('signupBoxCta', 'Jetzt Konto erstellen')
})

const signupCtaLink = computed(() => {
  if (props.targetRole === 'affiliated_pharmacy') {
    return globalConfig.linkRegisterPharmacy
  }
  return { name: 'signup' }
})

const destination = computed(() => {
  if (props.successUrl) {
    return props.successUrl
  }
  const url = route.query.destination
  if (url && typeof url === 'string' && !url.startsWith('http')) {
    return url
  }
  return null
})

const language = useCurrentLanguage()

const passwordResetRoute = computed(() => {
  return {
    name: 'user-password',
    query: {
      user: username.value || undefined,
    },
  }
})

if (providedUser.value) {
  username.value = providedUser.value
}

onMounted(() => {
  // Load the initial values in case the model wasn't updated by browser autofill.
  username.value =
    (document.querySelector('#username') as HTMLInputElement)?.value || ''
  password.value =
    (document.querySelector('#current-password') as HTMLInputElement)?.value ||
    ''
})

function submit() {
  if (isLoading.value) {
    return
  }
  isLoading.value = true
  useGraphqlMutation('userLogin', {
    username: username.value,
    password: password.value,
  })
    .then((v) => {
      // Login successful.
      const uid = v.data.userLogin?.uid

      // Check if login was successful.
      if (v.data.userLogin?.success) {
        // If a destination query param was provided, redirect to there.
        // This is the case when trying to access protected files or during the
        // oauth flow.
        if (destination.value) {
          window.location.href = destination.value
          return
        }

        // Redirect to user profile.
        window.location.href = `/${language.value}/user/${uid}`
        return
      }

      // Handle login errors. We should be receiving a message here. If not,
      // throw an error which will show a generic error message.
      if (!v.data.userLogin?.error) {
        throw new Error('Unexpected error')
      }

      const error = v.data.userLogin?.error

      // User is already logged in.
      if (error && error.includes('This route can only be accessed')) {
        // Redirect to the user profile.
        window.location.href = `/${language.value}/user`
        return
      }

      signinError.value = error
      isLoading.value = false
    })
    .catch(() => {
      isLoading.value = false
      // Show a generic error. This should only happen when Drupal is down or
      // throws a fatal.
      signinError.value = $texts(
        'loginError',
        'Unerwarteter Fehler. Bitte versuchen Sie es noch einmal oder kontaktieren Sie uns.',
      )
    })
}
</script>
